import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';

import GiveStartCauze from 'components/Asides/GiveStartCauze';
import { EventListItemWithActionMenu } from 'components/EventList/EventList';
import useDashboardStore from 'stores/DashboardStore';

const EntityCauzesView = ({
  isCompany,
  uiStore,
  matchStore,
  profileStore,
  joinedCauzes = [],
  activeCauzes = [],
  draftCauzes = [],
  endedCauzes = [],
  archivedCauzes = [],
  isAuthenticated,
  loading,
  updatePlannedEventState,
  userContext,
}) => {
  const { setProps } = useDashboardStore();
  const [activeMenuId, setActiveMenuId] = useState(null);

  // Initialize collapsed sections from localStorage
  const [collapsedSectionsByTitle, setCollapsedSectionsByTitle] = useState(
    () => {
      try {
        const stored = localStorage.getItem('cauze-sections-collapsed');
        const defaultState = { Archived: true }; // Default Archived section to collapsed

        if (stored) {
          const parsed = JSON.parse(stored);
          // Merge stored state with defaults, ensuring Archived is collapsed for new users
          return { ...defaultState, ...parsed };
        }

        return defaultState;
      } catch (error) {
        console.error(
          'Error reading collapsed sections from localStorage:',
          error,
        );
        return { Archived: true }; // Fallback to default state
      }
    },
  );

  const renderCauzeSection = (title, cauzes, hideActionMenu = false) => {
    if (!cauzes?.length) return <></>;

    const shouldHideActionMenu = (cauze) => {
      if (hideActionMenu) return true;

      if (userContext?.charityId) {
        if (+cauze?.hosts?.[0]?.id !== +userContext.charityId) {
          return true;
        }
      }

      return hideActionMenu;
    };

    const sectionId = `cauze-section-${title
      .toLowerCase()
      .replace(/\s+/g, '-')}`;
    const isCollapsed = !!collapsedSectionsByTitle[title];

    const toggleSection = () => {
      setCollapsedSectionsByTitle((prev) => {
        const newState = {
          ...prev,
          [title]: !prev[title],
        };

        // Save to localStorage
        try {
          localStorage.setItem(
            'cauze-sections-collapsed',
            JSON.stringify(newState),
          );
        } catch (error) {
          console.error(
            'Error saving collapsed sections to localStorage:',
            error,
          );
        }

        return newState;
      });
    };

    return (
      <>
        <div
          className={classnames(
            'text-left mb-5 font-agenda-bold text-[17px] leading-[17px] text-black cursor-pointer select-none',
            { 'mt-2': title === 'Joined', 'mt-5': title !== 'Joined' },
          )}
          role="button"
          tabIndex={0}
          aria-expanded={!isCollapsed}
          aria-controls={sectionId}
          onClick={toggleSection}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              toggleSection();
            }
          }}
        >
          <span
            className={classnames('inline-block transition-transform mr-2', {
              'rotate-[90deg]': !isCollapsed,
            })}
          >
            ▶
          </span>
          {title} ({cauzes.length})
        </div>
        {!isCollapsed && (
          <div
            id={sectionId}
            className="grid grid-cols-1 min-[390px]:grid-cols-2 gap-x-2 gap-y-3 event-list !p-0"
          >
            {cauzes &&
              cauzes.length > 0 &&
              cauzes.map((event) => (
                <EventListItemWithActionMenu
                  key={event.id}
                  event={event}
                  uiStore={uiStore}
                  loading={false}
                  hideActionMenu={shouldHideActionMenu(event)}
                  index={event.index}
                  activeMenuId={activeMenuId}
                  setActiveMenuId={setActiveMenuId}
                  matchStore={matchStore}
                  profileStore={profileStore}
                  userContext={userContext}
                  updatePlannedEventState={updatePlannedEventState}
                  isCompany={isCompany}
                />
              ))}
          </div>
        )}
      </>
    );
  };

  const isEmpty = useMemo(() => {
    return (
      !activeCauzes?.length &&
      !endedCauzes?.length &&
      !draftCauzes?.length &&
      !joinedCauzes?.length &&
      !archivedCauzes?.length
    );
  }, [activeCauzes, endedCauzes, draftCauzes, joinedCauzes, archivedCauzes]);

  useEffect(() => {
    setProps({
      title: 'Your Cauzes',
      asideChildren: isAuthenticated && <GiveStartCauze />,
      loading,
    });
  }, [isAuthenticated, loading]);

  if (!loading && isEmpty) {
    return <div className="text-center">No cauzes found.</div>;
  }

  const cauzesStartingSoon = activeCauzes.filter((cauze) =>
    moment(cauze.startDate).isAfter(moment()),
  );

  const activeCauzesAlreadyStarted = activeCauzes.filter(
    (cauze) => !moment(cauze.startDate).isAfter(moment()),
  );

  return (
    <div className="mb-16">
      {renderCauzeSection('Starting Soon', cauzesStartingSoon)}
      {renderCauzeSection('Active', activeCauzesAlreadyStarted)}
      {renderCauzeSection('Joined', joinedCauzes, true)}
      {renderCauzeSection('Drafts', draftCauzes)}
      {renderCauzeSection('Ended', endedCauzes)}
      {renderCauzeSection('Archived', archivedCauzes)}
    </div>
  );
};

export default EntityCauzesView;
